import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faBirthdayCake,
  faVenusMars,
  faGlobe,
  faGraduationCap,
  faWineGlass,
  faSmoking,
  faEdit,
  faXmark
} from "@fortawesome/free-solid-svg-icons";

import React, {useContext, useEffect, useState} from "react";
import InitContext from "../pages/InitContext";
import useMmcService from "../../services/MmcService";
import {FormControlLabel, Radio, RadioGroup} from "@mui/material";

const MedicalData = ({ patient }) => {
  const { t } = useTranslation();
  const context = useContext(InitContext);
  const [smokingDialogOpened, setSmokingDialogOpened] = useState(false);
  const [drinkingDialogOpened, setDrinkingDialogOpened] = useState(false);
  const [smoking, setSmoking] = useState(patient?.smoking_habit ?? "");
  const [drinking, setDrinking] = useState(patient?.drinking_habit ?? "");
  const [drinkingFrequency, setDrinkingFrequency] = useState(patient?.drinking_frequency ?? "");
  const { updateDbUser } = useMmcService();

  useEffect(() => {
    setSmoking(patient?.smoking_habit ?? "");
  }, [patient?.smoking_habit]);

  useEffect(() => {
    setDrinking(patient?.drinking_habit ?? "");
  }, [patient?.drinking_habit]);

  useEffect(() => {
    setDrinkingFrequency(patient?.drinking_frequency ?? "");
  }, [patient?.drinking_frequency]);

  const saveSmoking = () => {
    setSmokingDialogOpened(false);
    const patientData = { ...patient, smoking_habit: smoking };
    updateDbUser(context?.token, patientData, patient?.id).then();
  }

  const cancelEditingSmoking = () => {
    setSmoking(patient?.smoking_habit);
    setSmokingDialogOpened(false);
  }

  const cancelEditingDrinking = () => {
    setDrinking(patient?.drinking_habit);
    setDrinkingDialogOpened(false);
  }

  const saveDrinking = () => {
    setDrinkingDialogOpened(false);
    const patientData = { ...patient, drinking_habit: drinking, drinking_frequency: drinkingFrequency };
    updateDbUser(context?.token, patientData, patient?.id).then();
  }

  const smokingOptions = ["occasionally_regularly", "never", "previous"];
  const drinkingOptions = ["occasionally", "never", "previous"];

  const drinkingFrequencyOptions = [
    "daily_or_almost_daily",
    "three_or_four_times_a_week",
    "once_or_twice_a_week",
    "one_or_three_times_a_month",
  ];

  const medicalCards = [
    {
      key: "year_of_birth",
      icon: faBirthdayCake,
      label: t("year_of_birth"),
      value: patient?.year_of_birth || "-",
    },
    {
      key: "genotypic_sex",
      icon: faVenusMars,
      label: t("genotypic_sex"),
      value: patient?.gender ? t(patient?.gender) : "-",
    },
    {
      key: "ethnicity",
      icon: faGlobe,
      label: t("ethnicity"),
      value: patient?.ethnicity ? t(patient?.ethnicity?.toLowerCase()) : "-",
    },
    {
      key: "education",
      icon: faGraduationCap,
      label: t("highest_level_of_education"),
      value: patient?.education ? t(patient?.education) : "-",
      stackedLayout: true,
    },
    {
      key: "smoking",
      icon: faSmoking,
      label: t("smoking_habits"),
      value: smoking ? t(smoking).toLowerCase() : "-",
      onEdit: () => setSmokingDialogOpened(true),
    },
    {
      key: "drinking",
      icon: faWineGlass,
      label: t("alcohol_consuming"),
      value: `${drinking ? t(drinking).toLowerCase() : "-"}${drinkingFrequency && drinking !== "never" 
        ? ` (${t(drinkingFrequency).toLowerCase()})` 
        : ""}`,
      onEdit: () => setDrinkingDialogOpened(true),
      stackedLayout: true,
    },
  ];

  return (
    <div className="w3-container w3-left-align medical-data-view">
      {smokingDialogOpened && (
        <div className="feedback-modal">
          <div className="feedback-modal-content medical-data-dialog">
            <header className="feedback-modal-header">
              <h2>{t("select")}</h2>
              <FontAwesomeIcon
                icon={faXmark}
                className="modal-close"
                onClick={cancelEditingSmoking}
              />
            </header>
            <div className="feedback-modal-body">
              <RadioGroup
                aria-labelledby="smoking-group"
                name="smoking-group"
                value={smoking}
                onChange={(e => setSmoking(e.target.value))}
              >
                {smokingOptions?.map((radioValue, i) => {
                  return (
                    <div className="w3-container" key={`smoking${i}`}>
                      <FormControlLabel
                        key={`smokingValue${i}`}
                        value={radioValue}
                        control={<Radio />}
                        label={ t(radioValue) }
                      />
                    </div>
                  );
                })}
              </RadioGroup>
            </div>
            <div className="feedback-modal-buttons">
              <button onClick={saveSmoking} className="w3-btn w3-round-xlarge w3-theme-d5">
                {t("save")}
              </button>
            </div>
          </div>
        </div>
      )}
      {drinkingDialogOpened && (
        <div className="feedback-modal">
          <div className="alcohol-modal-content medical-data-dialog medical-data-dialog--wide">
            <div className="medical-data-dialog-grid">
              <div className="medical-data-dialog-section medical-data-dialog-section--primary">
                <header className="feedback-modal-header">
                  <h2>{t("your_alcohol_consumption")}</h2>
                  <FontAwesomeIcon
                    icon={faXmark}
                    className="modal-close"
                    onClick={cancelEditingDrinking}
                  />
                </header>
                <div className="feedback-modal-body">
                  <RadioGroup
                    aria-labelledby="drinking-group"
                    name="drinking-group"
                    value={drinking}
                    onChange={(e => setDrinking(e.target.value))}
                  >
                    {drinkingOptions?.map((radioValue, i) => {
                      return (
                        <div className="w3-container" key={`drinking${i}`}>
                          <FormControlLabel
                            key={`drinkingValue${i}`}
                            value={radioValue}
                            control={<Radio />}
                            label={ t(radioValue) }
                          />
                        </div>
                      );
                    })}
                  </RadioGroup>
                </div>
              </div>
              { (drinking === "occasionally" || drinking === "previous") &&
                <div className="medical-data-dialog-section medical-data-dialog-section--secondary">
                  <header className="feedback-modal-header">
                    <h2>{t("frequency")}</h2>
                  </header>
                  <div className="feedback-modal-body">
                    <RadioGroup
                      aria-labelledby="drinking-frequency-group"
                      name="drinking-frequency-group"
                      value={drinkingFrequency}
                      defaultValue={drinkingFrequency}
                      onChange={(e => setDrinkingFrequency(e.target.value))}
                    >
                        {drinkingFrequencyOptions?.map((radioValue, i) => {
                          return (
                            <div className="w3-container" key={`drinkingFrequency${i}`}>
                              <FormControlLabel
                                key={`drinkingFrequencyValue${i}`}
                                value={radioValue}
                                control={<Radio />}
                                label={ t(radioValue) }
                              />
                            </div>
                          );
                        })}
                    </RadioGroup>
                  </div>
                </div>
              }
            </div>
            <div className="feedback-modal-buttons">
              <button onClick={saveDrinking} className="w3-btn w3-round-xlarge w3-theme-d5">
                {t("save")}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="medical-data-grid">
        {medicalCards.map((item) => (
          <section
            className={`medical-data-card ${item.stackedLayout ? "medical-data-card--stacked" : ""}`}
            key={item.key}
          >
            <div className="medical-data-card__content">
              <span className="medical-data-card__icon" aria-hidden="true">
                <FontAwesomeIcon icon={item.icon} />
              </span>
              <span className="medical-data-card__label">{item.label}</span>
              <div className="medical-data-card__value">{item.value}</div>
            </div>
            {item.onEdit && (
              <button
                type="button"
                className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--secondary medical-data-inline-action"
                onClick={item.onEdit}
                aria-label={t("edit", { defaultValue: "Edit" })}
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
            )}
          </section>
        ))}
      </div>
    </div>
  );
};

export default MedicalData;
